<template>
  <div class="UsersList">
    <v-row class="header d-flex">
      <v-col class="col-12 col-sm-5 col-md-7 text-sm-left text-center">
        <div class="title">{{ $t('crm.userList.title') }}</div>
        <div
          v-if="isListLoaded"
          class="subtitle"
        >
          {{
            $tc('crm.userList.subtitle', usersLength, {
              clientsNumber: usersLength,
            })
          }}
        </div>
      </v-col>

      <v-col class="col-12 col-sm-7 col-md-5 text-sm-right text-center d-flex flex-sm-row flex-column">
        <TextInput
          v-model="search"
          clearable
          :placeholder="$t('crm.userList.client-search')"
          icon="mdi-magnify"
          class="flex-grow-1 flex-shrink-0 pr-sm-5 custom-placeholder"
        />
        <v-btn
          text
          class="theme-btn app-button-primary color nice-padding"
          @click="findPeople"
        >
          {{ $t('crm.userList.search') }}
        </v-btn>
      </v-col>
      <v-btn
        text
        class="theme-btn app-button-primary color nice-padding"
        @click="generateReport"
      >
        {{ $t('crm.userList.generate-btn') }}
      </v-btn>
    </v-row>
    <v-divider class="header-divider mt-8" />
    <v-row class="content mb-8">
      <v-data-table
        v-if="usersLength > 0"
        :headers="headers"
        :items="users"
        item-key="id"
        class="users-table"
        :items-per-page="10"
        :custom-sort="customSort"
        @update:page="fetchNextPage"
        @page-count="totalPages = $event"
      >
        <template v-slot:[`item.username`]="{ item }">
          <div class="users-link">
            <RouterLink :to="{ name: 'CustomerDetails', params: { id: item.id } }">
              <div v-if="item.first_name || item.last_name">{{ item.first_name }} {{ item.last_name }}</div>
              <div
                v-else
                class="not-specified"
              >
                {{ $t('common.not_specified') }}
              </div>
            </RouterLink>
          </div>
        </template>
        <template v-slot:[`item.unicia_person_id`]="{ item }">
          <div class="users-link">
            <RouterLink :to="{ name: 'CustomerDetails', params: { id: item.id } }">
              #{{ item.customer_files[0].amundi_client_reference || '0000' }}
            </RouterLink>
          </div>
        </template>
        <template v-slot:[`item.riskLevel`]="{ item }">
          <div :class="`indicator indicator--${getClass(item.risk_score)}`">
            {{ $t(`main.enums.risk.${item.risk_score}`) }}
            <span v-if="item.risk_score">
              -
              {{ item.risk_score }}
            </span>
          </div>
        </template>
        <template v-slot:[`item.last-update`]="{ item }">
          <div>
            {{ $d(new Date(item.meta.updated_at), 'short') }}
          </div>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <div class="text-capitalize">
            {{ item.customer_files[0].status | getStatus }}
          </div>
        </template>
        <template v-slot:[`item.last-cnx`]="{ item }">
          <div>
            {{ item.meta.updated_at | getTimeInterval }}
          </div>
        </template>
        <template v-slot:[`item.completion`]="{ item }">
          <CompletionCell :completion="item.meta.completion_rate" />
        </template>
        <template v-slot:[`item.user_consent`]="{ item }">
          <div v-if="item.user_consent">
            <v-icon color="success">mdi-account-check</v-icon>
          </div>
          <div v-else>
            <v-icon color="error">mdi-account-cancel</v-icon>
          </div>
        </template>
      </v-data-table>
    </v-row>
  </div>
</template>

<script>
  import TextInput from '@/components/ui/inputs/TextInput'
  import CompletionCell from '@/components/ui/table/CompletionCell'

  import CommonMixin from '@/mixins/store/CommonMixin'
  import ApiService from '@/services/ApiService'
  import i18n from '@/plugins/i18n'
  import { PERSON_STATUS } from '@/services/models.const'

  export default {
    components: {
      TextInput,
      CompletionCell,
    },
    filters: {
      getStatus(status) {
        if ([PERSON_STATUS.closed, PERSON_STATUS.completed,].includes(status)) {
          return i18n.t('main.enums.status.inactif')
        }

        return i18n.t('main.enums.status.actif')
      },
      getTimeInterval(date) {
        let seconds = Math.floor((Date.now() - new Date(date)) / 1000)

        if (seconds >= 31536000) {
          seconds = Math.floor(seconds / 31536000)

          return i18n.tc('common.time.year', seconds, { years: seconds, })
        } else if (seconds >= 2628000) {
          seconds = Math.floor(seconds / 2628000)

          return i18n.tc('common.time.month', seconds, { months: seconds, })
        } else if (seconds >= 604800) {
          seconds = Math.floor(seconds / 604800)

          return i18n.tc('common.time.week', seconds, { weeks: seconds, })
        } else if (seconds >= 86400) {
          seconds = Math.floor(seconds / 86400)

          return i18n.tc('common.time.day', seconds, { days: seconds, })
        } else if (seconds >= 3600) {
          seconds = Math.floor(seconds / 3600)

          return i18n.tc('common.time.hour', seconds, { hours: seconds, })
        } else if (seconds >= 60) {
          seconds = Math.floor(seconds / 60)

          return i18n.tc('common.time.minute', seconds, { minutes: seconds, })
        } else {
          return i18n.tc('common.time.second', seconds, { seconds: seconds, })
        }
      },
    },
    mixins: [CommonMixin,],
    data() {
      return {
        search: '',
        page: 1,
        itemsParPage: 50,
        totalPages: 0,
        filterMenu: false,
        users: [],
        isListLoaded: false,
        headers: [
          {
            text: `${this.$t('crm.userList.username')}`,
            value: 'username',
            align: 'left',
          },
          {
            text: `${this.$t('crm.userList.id-document')}`,
            value: 'unicia_person_id',
            align: 'right',
          },
          {
            text: `${this.$t('crm.userList.risk-level')}`,
            value: 'riskLevel',
            align: 'right',
          },
          {
            text: `${this.$t('crm.userList.raises-number')}`,
            value: 'customer_files[0].reminders_sent',
            align: 'right',
          },
          {
            text: `${this.$t('crm.userList.status')}`,
            value: 'status',
            align: 'right',
          },
          {
            text: `${this.$t('crm.userList.last-update')}`,
            value: 'last-update',
            align: 'right',
          },
          {
            text: `${this.$t('common.last-cnx')}`,
            value: 'last-cnx',
            align: 'right',
          },
          { text: `${this.$t('crm.userList.completion')}`, value: 'completion', },
          { text: `${this.$t('crm.userList.consent')}`, value: 'user_consent', },
        ],
      }
    },
    computed: {
      usersLength() {
        return Object.keys(this.users).length
      },
    },
    watch: {
      search() {
        if (!this.search || this.search.length === 0) {
          this.fetchList()
        }
      },
    },
    async mounted() {
      await this.fetchList()
    },
    methods: {
      async generateReport() {
        await ApiService.generateReport(localStorage.getItem('advisor_mail'))
      },
      customSort(items, index, isDesc) {
        items.sort((a, b) => {
          if (index[0] === 'last-update' || index[0] === 'last-cnx') {
            return this.compareDates(a.meta.updated_at, b.meta.updated_at, isDesc[0])
          } else if (index[0] === 'completion') {
            return this.compareElements(a.meta.completion_rate, b.meta.completion_rate, isDesc[0])
          } else if (index[0] === 'status') {
            return this.compareElements(a.customer_files[0].status, b.customer_files[0].status, isDesc[0])
          } else if (index[0] === 'username') {
            return this.compareElements(a.first_name, b.first_name, isDesc[0])
          } else if (index[0] === 'riskLevel') {
            return this.compareElements(a.risk_score, b.risk_score, isDesc[0])
          } else if (index[0] === 'unicia_person_id') {
            return this.compareElements(
              a.customer_files[0].amundi_client_reference,
              b.customer_files[0].amundi_client_reference,
              isDesc[0]
            )
          } else {
            return this.compareElements(a[index[0]], b[index[0]], isDesc[0])
          }
        })

        return items
      },
      compareElements(elem1, elem2, isDesc) {
        if (!isDesc) {
          return elem1 < elem2 ? -1 : 1
        } else {
          return elem2 < elem1 ? -1 : 1
        }
      },
      compareDates(d1, d2, isDesc) {
        if (!isDesc) {
          return new Date(d1) - new Date(d2)
        } else {
          return new Date(d2) - new Date(d1)
        }
      },
      async findPeople() {
        this.fetchList(this.search)
      },
      async fetchList(search = '', appendList = false) {
        this.toggleAppLoader(true)
        if (search.length) {
          this.page = 1
        }
        const result = await ApiService.getPeopleList({
          search,
          page: this.page,
          perPage: this.itemsParPage,
        })
        this.users = appendList ? [...this.users, ...result.data,] : result.data
        this.isListLoaded = true
        this.toggleAppLoader(false)
      },
      fetchNextPage(currentPage) {
        if (currentPage === this.totalPages) {
          this.page++
          this.fetchList('', true)
        }
      },
      getClass(property) {
        const indicator = parseInt(property, 2)
        if (indicator === 1 || indicator === 2) {
          return 'low'
        } else if (indicator === 3) {
          return 'medium'
        } else if (indicator === 4) {
          return 'high'
        } else return 'no-risk'
      },
    },
  }
</script>

<style lang="scss">
.UsersList {
  margin: 2.5rem;
  padding: 2rem;
  box-shadow: 0 4px 8px 0 rgba(31, 54, 139, 0.08);
  border-radius: 8px;
  background-color: $color-ffffff;
  .header {
    .title {
      color: $color-001c4b;
      font-size: 1.25em;
      font-weight: 500;
      line-height: 2em;
    }
    .subtitle {
      color: $color-4d4d4f;
      font-size: 1em;
      letter-spacing: 0.24px;
      line-height: 1.5em;
    }
  }
  .header-divider {
    background: rgba(158, 159, 162, 0.32);
    margin-bottom: 47px;
  }
  .content {
    height: 100%;
    padding-left: 10px;
    .users-table {
      width: 100%;
      .v-data-table__wrapper
      > table
      > tbody
      > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
        background: $color-transparent;
      }
      .v-data-table__mobile-row {
        display: block;
        .documents-select {
          width: 12em;
        }
      }
      tbody > tr:nth-of-type(odd) {
        background-color: $color-fafbff;
      }
      td,
      th {
        border-bottom: 1px solid $color-217_223_255 !important;
      }
      th {
        color: $color-009ee0 !important;
        text-transform: uppercase;
        font-weight: 500;
      }
      .users-link {
        a {
          color: $color-009ee0 !important;
          text-decoration: underline !important;
        }
        .not-specified {
          color: $color-5e5e5e !important;
          text-decoration: underline !important;
        }
      }
      .indicator {
        display: inline;
        padding: 4px 8px;
        font-size: 0.8rem;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 20px;
        text-align: center;
        color: $color-ffffff;
        text-transform: uppercase;
        border-radius: 4px;

        &--high {
          background-color: $color-ff524f;
        }
        &--medium {
          background-color: $color-f07d00;
        }
        &--low {
          background-color: $color-009ee0;
        }
        &--no-risk {
          background-color: $color-5e5e5e;
        }
      }
    }
  }
}
.custom-placeholder input::placeholder {
  color: $color-004f9f !important;
}
</style>
